'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Map, useMap } from '@vis.gl/react-google-maps';
import { StrategyMapResults } from '../StrategyMapResults';
import {
  StrategyMapContent,
  StrategyMapMarkers,
  StrategyMapControls,
  HeatmapControls,
} from './components';

import { LOCATION } from 'src/hooks/filters/constants';
import useStrategies from 'src/hooks/strategy/useStrategies';
import useStrategyMetricSort from 'src/hooks/strategy/useStrategyMetricSort';
import useRouteState from 'src/hooks/routes/useRouteState';
import useUserLocation from 'src/hooks/geolocation/useUserLocation';
import { useLogging } from 'src/context/logging';
import {
  useGetMyChainedStrategiesQuery,
  useGetStrategyMetricsQuery,
} from 'src/api/redux';
import {
  findSelectedChain,
  getTargetStrategyIdString,
} from 'src/utils/strategyHelpers';
import { MAP_RESULTS_FALLBACK_SORT, MAX_ZOOM, MIN_ZOOM } from './constants';
import { useDebouncedCallback } from 'use-debounce';

import { AddToListModal } from 'src/components';
import { ActiveRouteOverlay } from 'src/components/Routes/layers/ActiveRouteOverlay';
import { MyLocationButton, UserLocationMarker } from 'src/components/Maps';
import { roundCoordinate } from 'src/components/Maps/roundCoordinate';
import { MedScoutMapsProvider } from 'src/components/Maps/MedScoutMapsProvider';
import { useFlags } from 'launchdarkly-react-client-sdk';

const USER_LOCATION_ZOOM = 10;

/**
 * Pans the map to the user's location when acquired.
 * Must be rendered inside a <Map> component (needs useMap()).
 * Follows the same child-component pattern as ActiveRouteOverlay.
 */
const PanToUserLocation = ({
  location,
  isRouteActive,
}: {
  location: { lat: number; lng: number } | null;
  isRouteActive: boolean;
}) => {
  const map = useMap();
  const handledRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (!map || !location || isRouteActive) {
      // Reset when location is cleared so re-acquiring the same coords still pans
      if (!location) handledRef.current = null;
      return;
    }
    const key = `${location.lat},${location.lng}`;
    if (key === handledRef.current) return;
    handledRef.current = key;
    map.panTo(location);
    map.setZoom(USER_LOCATION_ZOOM);
  }, [map, location, isRouteActive]);

  return null;
};

interface StrategyMapProps {
  search: string;
}

const StrategyMap = ({ search }: StrategyMapProps) => {
  const log = useLogging();
  const [showAddToListDialog, setShowAddToListDialog] = useState(false);
  // Shared "select all" resolution state, reported up from the results panel so
  // marker add-paths and this modal block until the full set is resolved.
  const [isResolvingSelection, setIsResolvingSelection] = useState(false);
  const { useWebRouting, showEncountersTotals } = useFlags();
  const {
    selectedChainIds,
    selectedTerritory,
    boundedTerritory,
    zoom,
    center,
    setZoom,
    setCenter,
    setBounds,
    selectedProviders,
    clearSelectedProviders,
    activeMarker,
    setActiveMarker,
  } = useStrategies(LOCATION.STRATEGY_CENTER);

  // The sort is owned here; per-strategy metrics only apply to a single chain.
  const { data: chainedStrategies, isLoading: chainedStrategiesLoading } =
    useGetMyChainedStrategiesQuery();
  const selectedChain = useMemo(
    () => findSelectedChain(selectedChainIds, chainedStrategies?.results),
    [selectedChainIds, chainedStrategies?.results]
  );
  const strategyId = getTargetStrategyIdString(selectedChain);
  const { currentData: strategyMetrics, isError: strategyMetricsError } =
    useGetStrategyMetricsQuery(
      { strategyType: 'target', strategyId },
      { skip: !strategyId }
    );
  const isSingleChain = selectedChainIds.length === 1;
  const singleChainMetrics = isSingleChain ? strategyMetrics?.results : undefined;
  // `currentData` is undefined again after switching chains (RTK's `isLoading` is
  // only true for a hook's first load), so wait until the new chain's metrics land
  // or fail, rather than briefly sorting by the fallback.
  const metricsLoading =
    isSingleChain &&
    (strategyId
      ? !strategyMetrics && !strategyMetricsError
      : !!chainedStrategiesLoading);
  const { sortValues, setSortValues, sortReady, sortParams } =
    useStrategyMetricSort({
      selectedChainIds,
      strategyMetrics: singleChainMetrics,
      metricsLoading,
      fallbackSort: MAP_RESULTS_FALLBACK_SORT,
      showEncountersTotals: !!showEncountersTotals,
    });

  // User location
  const {
    location: userLocation,
    permission: locationPermission,
    isLocating,
    requestLocation,
    clearLocation,
  } = useUserLocation();

  // Route state
  const { isCreating, isEditing, isViewing } = useRouteState();
  const isRouteActive = isCreating || isEditing || isViewing;

  const handleOpenAddToList = useCallback(() => {
    setShowAddToListDialog(true);
  }, []);

  const handleAddToListSuccess = useCallback(
    (result: { addedCount: number; targetListName: string }) => {
      log.event('Providers added to list from map marker', {
        listName: result.targetListName,
        count: result.addedCount,
      });
      clearSelectedProviders();
    },
    [log, clearSelectedProviders]
  );

  // Handle camera changes — debounced at 500ms to prevent per-frame Redux/API spam.
  // With defaultCenter (uncontrolled), the map manages its own position during
  // pan/drag. Redux receives a snapshot only after the gesture settles.
  const handleMapViewChanged = useCallback(
    (event: any) => {
      if (!event?.detail) return;

      const {
        center: newCenter,
        zoom: newZoom,
        bounds: newBounds,
      } = event.detail;

      if (newZoom !== undefined) {
        setZoom(newZoom);
      }

      if (newCenter?.lat !== undefined && newCenter?.lng !== undefined) {
        setCenter(newCenter);
      }

      if (newBounds) {
        const { north, south, east, west } = newBounds;

        if (north && south && east && west) {
          setBounds({
            north: roundCoordinate(north),
            south: roundCoordinate(south),
            east: roundCoordinate(east),
            west: roundCoordinate(west),
          });
        }
      }
    },
    [setZoom, setCenter, setBounds]
  );

  const debouncedHandleMapViewChanged = useDebouncedCallback(
    handleMapViewChanged,
    500
  );

  const handleDialogClose = useCallback(() => {
    setShowAddToListDialog(false);
    clearSelectedProviders();
  }, [clearSelectedProviders]);

  // Debounce only the logging
  const debouncedLog = useDebouncedCallback((zoom: number) => {
    log.event('Strategy map zoom changed', {
      source: 'StrategyMap',
      zoomLevel: zoom,
      selectedChainIds,
      territoryId: selectedTerritory?.id,
    });
  }, 500);

  useEffect(() => {
    debouncedLog(zoom);
  }, [zoom, debouncedLog]);

  return (
    <div data-testid="strategy-map" className="flex w-full h-full bg-slate-100">
      {/* Map */}
      <MedScoutMapsProvider>
        <div className="relative flex-1 h-full">
          <Map
            mapId="strategy-map"
            className="w-full h-full"
            disableDefaultUI
            gestureHandling={'greedy'}
            defaultCenter={center}
            zoom={zoom}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            onCameraChanged={debouncedHandleMapViewChanged}
            scrollwheel
            zoomControl={false}
            zoomControlOptions={undefined}
          >
            <StrategyMapMarkers
              search={search}
              onAddToList={handleOpenAddToList}
              sortValues={sortValues}
              strategyMetrics={singleChainMetrics}
            />
            <StrategyMapContent />

            <StrategyMapControls />
            <HeatmapControls />

            {/* User location — pan map when location is acquired */}
            <PanToUserLocation
              location={userLocation}
              isRouteActive={isRouteActive}
            />
            {userLocation && (
              <UserLocationMarker
                lat={userLocation.lat}
                lng={userLocation.lng}
              />
            )}

            {/* Route rendering */}
            {useWebRouting && <ActiveRouteOverlay />}
          </Map>

          {/* My Location button — bottom-right of map */}
          <MyLocationButton
            userLocation={userLocation}
            isLocating={isLocating}
            permission={locationPermission}
            onRequestLocation={requestLocation}
            onClearLocation={clearLocation}
          />
        </div>
      </MedScoutMapsProvider>

      {/* Right: Results panel */}
      <StrategyMapResults
        search={search}
        sortValues={sortValues}
        sortParams={sortParams}
        sortReady={sortReady}
        onSortChange={setSortValues}
        strategyMetrics={singleChainMetrics}
        setShowAddToListDialog={setShowAddToListDialog}
        onResolvingChange={setIsResolvingSelection}
      />
      <AddToListModal
        isOpen={showAddToListDialog}
        onClose={handleDialogClose}
        mode="add"
        providers={selectedProviders}
        isResolving={isResolvingSelection}
        onSuccess={handleAddToListSuccess}
      />
    </div>
  );
};

export default StrategyMap;
